import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import Loader from "../../common/loader";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import * as verificationServices from "../../services/verification.services";

/*
 * Verification Requests: astrologers send their past consultations (count
 * plus proof files) and past client reviews (with a screenshot) from the
 * astrologer portal. Nothing shows on the public profile until it is
 * approved here. A rejection needs a reason, which the astrologer sees.
 */

const fmtDate = (v) =>
  v
    ? new Date(String(v).replace(" ", "T")).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const Stars = ({ value }) => (
  <span aria-label={`${value} out of 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <i key={i} className="fa fa-star" style={{ color: i < value ? "#FFC107" : "#E4E6EF", marginRight: 2 }} />
    ))}
  </span>
);

const VerificationList = () => {
  const [type, setType] = useState("consultations");
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ list: [], total: 0, per_page: 20, pending: { consultations: 0, reviews: 0 } });
  const [loader, setLoader] = useState(true);
  const [refresh, setRefresh] = useState(0);

  const breadcrumbs = [
    { title: "Dashboard", url: "/admin/dashboard" },
    { title: "Verification Requests", url: "" },
  ];

  useEffect(() => {
    setLoader(true);
    verificationServices
      .List({ type, status, page })
      .then((res) => {
        if (res && res.success) setData(res.data);
        else Swal.fire({ icon: "error", text: res?.message || "Could not load requests", ...SWAL_SETTINGS });
      })
      .catch(() => {})
      .finally(() => setLoader(false));
  }, [type, status, page, refresh]);

  const reload = () => setRefresh((n) => n + 1);

  const done = (res) => {
    if (res && res.success) {
      Swal.fire({ icon: "success", text: res.message, ...SWAL_SETTINGS });
      reload();
    } else {
      Swal.fire({ icon: "error", text: res?.message || "Something went wrong", ...SWAL_SETTINGS });
    }
  };

  const openProof = useCallback(async (params) => {
    // Open the tab first (inside the click) so the browser does not block it.
    const tab = window.open("", "_blank");
    try {
      const blob = await verificationServices.Proof(params);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (tab) tab.close();
      Swal.fire({ icon: "error", text: "Could not open the file", ...SWAL_SETTINGS });
    }
  }, []);

  const approveConsultations = async (row) => {
    const { isConfirmed, value } = await Swal.fire({
      title: "Approve Past Consultations",
      html: `<p class="mb-2">${row.astrologer_name} asked for <b>${Number(row.requested_consultations).toLocaleString("en-IN")}</b>. Correct the number if the proof shows less.</p>`,
      input: "number",
      inputValue: row.requested_consultations,
      inputAttributes: { min: 0, max: 10000000, step: 1 },
      showCancelButton: true,
      confirmButtonText: "Approve",
      inputValidator: (v) => (!/^\d+$/.test(String(v)) ? "Enter a whole number" : undefined),
    });
    if (!isConfirmed) return;
    done(await verificationServices.ApproveConsultations({ astrologer_id: row.astrologer_id, count: value }));
  };

  const askReason = async (title) => {
    const { isConfirmed, value } = await Swal.fire({
      title,
      input: "textarea",
      inputPlaceholder: "Reason (the astrologer will see this)",
      inputAttributes: { maxlength: 500 },
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#d33",
      inputValidator: (v) => (!v || v.trim().length < 3 ? "Please write the reason" : undefined),
    });
    return isConfirmed ? value.trim() : null;
  };

  const rejectConsultations = async (row) => {
    const reason = await askReason("Reject Past Consultations");
    if (reason) done(await verificationServices.RejectConsultations({ astrologer_id: row.astrologer_id, reason }));
  };

  const approveReview = async (row) => {
    const { isConfirmed } = await Swal.fire({
      title: "Approve This Review?",
      text: `It will show on ${row.astrologer_name}'s profile under "${row.reviewer_name}", dated ${fmtDate(row.review_date)}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    });
    if (isConfirmed) done(await verificationServices.ApproveReview({ id: row.id }));
  };

  const rejectReview = async (row) => {
    const reason = await askReason("Reject This Review");
    if (reason) done(await verificationServices.RejectReview({ id: row.id, reason }));
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.per_page || 20)));
  const isPending = status === "pending";

  const proofButtons = (row) =>
    Array.from({ length: row.proof_count || 0 }).map((_, i) => (
      <button
        key={i}
        type="button"
        className="btn btn-sm btn-outline-primary me-1 mb-1"
        onClick={() =>
          openProof(type === "reviews" ? { type, id: row.id } : { type, id: row.astrologer_id, index: i })
        }
      >
        <i className="fa fa-paperclip me-1" />
        File {row.proof_count > 1 ? i + 1 : ""}
      </button>
    ));

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
              <h6 className="main-content-label mb-0">Verification Requests</h6>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div className="btn-group" role="group" aria-label="Request type">
                  <button
                    type="button"
                    className={`btn ${type === "consultations" ? "btn-main-primary" : "btn-outline-secondary"}`}
                    aria-pressed={type === "consultations"}
                    onClick={() => { setType("consultations"); setPage(1); }}
                  >
                    Past Consultations ({data.pending?.consultations ?? 0})
                  </button>
                  <button
                    type="button"
                    className={`btn ${type === "reviews" ? "btn-main-primary" : "btn-outline-secondary"}`}
                    aria-pressed={type === "reviews"}
                    onClick={() => { setType("reviews"); setPage(1); }}
                  >
                    Past Reviews ({data.pending?.reviews ?? 0})
                  </button>
                </div>
                <div className="form-group mb-0 filter_icon_container filter_design">
                  <i className="fa fa-filter mr-2 filter_icon"></i>
                  <div className="select-down-arrow">
                    <select
                      className="form-control cp"
                      value={status}
                      aria-label="Status"
                      onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered w-100">
                <thead>
                  {type === "consultations" ? (
                    <tr>
                      <th>Astrologer</th>
                      <th>Requested</th>
                      <th>Current (Approved)</th>
                      <th>Note</th>
                      <th>Proof</th>
                      <th>Sent On</th>
                      <th>{isPending ? "Actions" : "Result"}</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>Astrologer</th>
                      <th>Client</th>
                      <th>Rating</th>
                      <th>Review</th>
                      <th>Review Date</th>
                      <th>Proof</th>
                      <th>{isPending ? "Actions" : "Result"}</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan="7" className="text-center"><Loader /></td>
                    </tr>
                  ) : data.list.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">No {status} requests.</td>
                    </tr>
                  ) : type === "consultations" ? (
                    data.list.map((row) => (
                      <tr key={row.astrologer_id}>
                        <td>
                          <strong>{row.astrologer_name}</strong>
                          {row.phone_number ? <div className="small">{row.phone_number}</div> : null}
                        </td>
                        <td><strong>{Number(row.requested_consultations || 0).toLocaleString("en-IN")}</strong></td>
                        <td>{Number(row.past_consultations || 0).toLocaleString("en-IN")}</td>
                        <td style={{ maxWidth: 260, whiteSpace: "normal" }}>{row.request_note || "—"}</td>
                        <td>{proofButtons(row)}</td>
                        <td>{fmtDate(row.requested_at)}</td>
                        <td>
                          {isPending ? (
                            <>
                              <button type="button" className="btn btn-sm btn-success me-1 mb-1" onClick={() => approveConsultations(row)}>Approve</button>
                              <button type="button" className="btn btn-sm btn-danger mb-1" onClick={() => rejectConsultations(row)}>Reject</button>
                            </>
                          ) : (
                            <span style={{ whiteSpace: "normal" }}>
                              {status === "approved" ? "Approved" : `Rejected: ${row.reject_reason || ""}`}
                              <div className="small">{fmtDate(row.reviewed_at)}</div>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    data.list.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.astrologer_name}</strong>
                          {row.phone_number ? <div className="small">{row.phone_number}</div> : null}
                        </td>
                        <td><strong>{row.reviewer_name}</strong></td>
                        <td><Stars value={Number(row.rating)} /></td>
                        <td style={{ maxWidth: 320, whiteSpace: "normal" }}>{row.review}</td>
                        <td>{fmtDate(row.review_date)}</td>
                        <td>{proofButtons(row)}</td>
                        <td>
                          {isPending ? (
                            <>
                              <button type="button" className="btn btn-sm btn-success me-1 mb-1" onClick={() => approveReview(row)}>Approve</button>
                              <button type="button" className="btn btn-sm btn-danger mb-1" onClick={() => rejectReview(row)}>Reject</button>
                            </>
                          ) : (
                            <span style={{ whiteSpace: "normal" }}>
                              {status === "approved" ? "Approved" : `Rejected: ${row.reject_reason || ""}`}
                              <div className="small">{fmtDate(row.reviewed_at)}</div>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default VerificationList;
